import {
  Grid,
  GridItem,
  Input,
  Switch,
  InputGroup,
  Box,
  InputLeftAddon,
  SimpleGrid,
  InputRightAddon
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import React, { useEffect, useMemo, useState } from 'react'

import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { LoadingBlock } from '../../../../components/Loading'
import { MoneyInput } from '../../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { TabErrorLabel } from '../../../../components/TabErrorLabel'
import { formatNumber, calculatePmt, useAppToast } from '../../../../helpers'
import {
  countErrorsPerTab,
  firstTabWithErrors,
  ITabDef,
  unmappedErrorMessages
} from '../../../../helpers/tabErrorUtils'
import { ICoursesInPerson } from '../../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { CoursesOnlineService } from '../../../../services/CoursesOnline/CoursesOnlineService'
import { IRecordedCoursePageFields } from '../../../../services/CoursesOnline/interfaces'
import { TeacherService } from '../../../../services/Teacher/TeacherService'
import { FaqEditor, validateFaqs } from '../../Events/FaqEditor'
import { CourseModulesEditor, validateModules } from '../Courses/CourseModulesEditor'
import { CoursePageTab } from '../Courses/CoursePageTab'
import { CourseBonusesEditor, validateBonuses } from './CourseBonusesEditor'
import { CourseOnlinePassaporteTab } from './CourseOnlinePassaporteTab'
import { CourseOnlineTeachersTab, validateTeachers } from './CourseOnlineTeachersTab'

/** A API responde preço como string decimal do banco (`'1234.50'`); o formulário guarda o número. */
const toPrice = (value?: string | null): number | null => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export interface IFormData extends IRecordedCoursePageFields {
  name: string
  thumbnail: string | File | null
  course_link: string
  eduzz_url?: string
  /** Pacote "Passaporte" da campanha Monte seu Kit (ABRA-406), na aba Passaporte. */
  package_enabled: boolean
  /** Nome do passaporte (ABRA-449), que vai como curso do lead do kit. Vazio, vai "Passaporte". */
  package_name: string
  eduzz_package_url: string
  package_price: number | null
  package_course_ids: number[]
  full_price_recorded: number | null
  discount_percentage_recorded: string
  cta_text_recorded: string
  duration_in_hours: string | number
  duration_in_months: string | number
  promotional_bar: string
  cronometer_type?: string
  cronometer_end_date: string
  cronometer_time: string
  /** Título do cronômetro da seção do curso no GreatPages (ABRA-439), independente da Barra de Promoção. */
  cronometer_title: string
  in_person: boolean
  live_online: boolean
  active_campaign_lists: string[]
  active_campaign_in_person_lists: string[]
  active_campaign_live_online_lists: string[]
  dynamic_ids: string[]
  objective_ids: string[]
  knowledge_area_ids: string[]
}

const emptyForm = (): IFormData => ({
  name: '',
  thumbnail: null,
  course_link: '',
  eduzz_url: '',
  package_enabled: true,
  package_name: '',
  eduzz_package_url: '',
  package_price: null,
  package_course_ids: [],
  full_price_recorded: null,
  discount_percentage_recorded: '0',
  cta_text_recorded: '',
  duration_in_hours: '0',
  duration_in_months: '0',
  promotional_bar: '0',
  cronometer_type: '0',
  cronometer_end_date: '0',
  cronometer_time: '0',
  cronometer_title: '',
  in_person: false,
  live_online: false,
  active_campaign_lists: [],
  active_campaign_in_person_lists: [],
  active_campaign_live_online_lists: [],
  dynamic_ids: [],
  objective_ids: [],
  knowledge_area_ids: [],
  hero_description: '',
  hero_video_url: '',
  about: '',
  prerequisites: '',
  age_type: '',
  age_value: '',
  modules: [],
  faqs: [],
  bonuses: [],
  teacher_ids: [],
  tutor_image: null,
  remove_tutor_image: false
})

/**
 * Os campos de cada aba, só para levar o erro da API até a aba que o mostra. Da terceira em diante
 * é a página do curso gravado no site (ABRA-437); todo gravado tem página, então elas não somem.
 */
const COURSE_ONLINE_TAB_DEFS: ITabDef[] = [
  {
    key: 'general',
    label: 'Geral',
    fields: [
      'name',
      'thumbnail',
      'course_link',
      'eduzz_url',
      'knowledge_area_ids',
      'dynamic_ids',
      'active_campaign_lists',
      'objective_ids',
      'full_price_recorded',
      'discount_percentage_recorded',
      'cta_text_recorded',
      'duration_in_hours',
      'duration_in_months',
      'promotional_bar',
      'cronometer_type',
      'cronometer_end_date',
      'cronometer_time',
      'cronometer_title'
    ]
  },
  {
    key: 'passaporte',
    label: 'Passaporte',
    fields: ['package_enabled', 'package_name', 'eduzz_package_url', 'package_price', 'package_course_ids']
  },
  {
    key: 'page',
    label: 'Página do curso',
    fields: ['hero_description', 'hero_video_url', 'about', 'prerequisites', 'age_type', 'age_value']
  },
  { key: 'modules', label: 'Módulos', fields: ['modules'] },
  { key: 'bonuses', label: 'Bônus', fields: ['bonuses'] },
  { key: 'teachers', label: 'Professores', fields: ['teacher_ids', 'tutor_image', 'remove_tutor_image'] },
  { key: 'faqs', label: 'Perguntas frequentes', fields: ['faqs'] }
]

const CoursesOnlineModal = ({
  isOpen,
  onClose,
  editCourse,
  onSuccess
}: {
  isOpen: boolean
  onClose: () => void
  editCourse?: Partial<ICoursesInPerson>
  onSuccess?: () => void
}) => {
  const toast = useAppToast()
  const service = new CoursesOnlineService()
  const teacherService = new TeacherService()

  const [knowledgeAreas, setKnowledgeAreas] = useState<{ id: number; name: string }[]>([])
  const [dynamics, setDynamics] = useState<{ id: number; name: string }[]>([])
  const [objectives, setObjectives] = useState<{ id: number; name: string }[]>([])
  const [activeCampaignLists, setActiveCampaignLists] = useState<{ id: string; name: string }[]>([])
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})
  const [packageCourseOptions, setPackageCourseOptions] = useState<{ id: number; name: string }[]>([])
  const [savedPackageCourses, setSavedPackageCourses] = useState<{ id: number; name: string }[]>([])
  const [teacherOptions, setTeacherOptions] = useState<{ id: number; name: string }[]>([])
  const [loadingTeachers, setLoadingTeachers] = useState(true)
  const [savedTeachers, setSavedTeachers] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)

  // Single source of truth lives in API config/pricing.php; we hydrate from
  // GET /api/website/courses/recorded/installment-config on mount.
  // Defaults match the back's defaults so the preview never flickers from 0.
  const [installmentConfig, setInstallmentConfig] = useState<{ monthly_rate: number; installments: number }>({
    monthly_rate: 0.0349,
    installments: 12
  })

  useEffect(() => {
    service.getKnowledgeAreas().then(setKnowledgeAreas)
    service.getDynamics().then(setDynamics)
    service.getActiveCampaignLists().then(setActiveCampaignLists)
    service.getObjectives().then(setObjectives)
    service.listForSelect().then(setPackageCourseOptions)
    // Inclui os desativados: o site mostra o tutor do curso mesmo inativo, só sem o link do perfil.
    teacherService
      .listForSelectIncludingDisabled()
      .then(setTeacherOptions)
      .finally(() => setLoadingTeachers(false))
    service
      .getRecordedInstallmentConfig()
      .then(setInstallmentConfig)
      .catch(() => {
        // Fallback silencioso: mantém os defaults do useState (3,49% / 12x) se
        // a API estiver indisponível — o preview continua funcionando.
      })
  }, [])

  const [formData, setFormData] = useState<IFormData>(emptyForm)

  useEffect(() => {
    // Salvar fecha o modal sem passar pelo `clearForm`: o próximo curso abre na aba Geral.
    setTabIndex(0)
    setErrors({})
    setSavedPackageCourses([])
    setSavedTeachers([])
    if (editCourse?.id) {
      loadCourse(editCourse.id)
    } else {
      setFormData(emptyForm())
    }
  }, [editCourse])

  const loadCourse = async (id: number) => {
    setLoading(true)
    // `finally`, e não um `setLoading(false)` depois do await: uma falha em `getCourse` deixava
    // o modal girando para sempre. E o modal fecha: o formulário vazio, salvo, apagaria o curso.
    let course
    try {
      course = await service.getCourse(id)
    } catch (error) {
      toast.error('Erro ao carregar curso', error)
      clearForm()
      return
    } finally {
      setLoading(false)
    }
    setSavedPackageCourses(course.package_courses ?? [])
    setSavedTeachers(course.teachers ?? [])

    setFormData({
      name: course.name || '',
      thumbnail: course.thumbnail || null,
      course_link: course.course_link || '',
      eduzz_url: course.eduzz_url || '',
      package_enabled: course.package_enabled ?? true,
      package_name: course.package_name || '',
      eduzz_package_url: course.eduzz_package_url || '',
      package_price: toPrice(course.package_price),
      // O próprio curso entra no pacote e conta no limite: sem pacote, ele já vem marcado.
      package_course_ids: course.package_course_ids?.length ? course.package_course_ids : [course.id],
      full_price_recorded: toPrice(course.price_recorded?.price_original),
      discount_percentage_recorded: formatNumber(course.price_recorded?.discount?.replace('.', ',') || '0') || '0',
      cta_text_recorded: course.cta_text_recorded || '',
      duration_in_hours: course.duration_in_hours || '0',
      duration_in_months: course.duration_in_months || '0',
      promotional_bar: course.promotional_bar || '0',
      cronometer_type: course.cronometer_type || '0',
      cronometer_end_date: course.cronometer_end_date || '0',
      cronometer_time: course.cronometer_time || '0',
      cronometer_title: course.cronometer_title || '',
      in_person: false,
      live_online: false,
      active_campaign_lists: course.active_campaign_recorded_lists || [],
      active_campaign_in_person_lists: [],
      active_campaign_live_online_lists: [],
      dynamic_ids: course.dynamics.map((d) => d.id.toString()) || [],
      objective_ids: course.objectives.map((o) => o.id.toString()) || [],
      knowledge_area_ids: course.knowledge_areas.map((k) => k.id.toString()) || [],
      hero_description: course.hero_description || '',
      hero_video_url: course.hero_video_url || '',
      about: course.about || '',
      prerequisites: course.prerequisites || '',
      age_type: course.age_type || '',
      age_value: course.age_value || '',
      modules: (course.modules || []).map((m) => ({
        _id: crypto.randomUUID(),
        id: m.id,
        title: m.title,
        content: m.content || ''
      })),
      faqs: (course.faqs || []).map((f) => ({
        _id: crypto.randomUUID(),
        id: f.id,
        question: f.question,
        answer: f.answer
      })),
      bonuses: (course.bonuses || []).map((b) => ({
        _id: crypto.randomUUID(),
        id: b.id,
        title: b.title || '',
        description: b.description
      })),
      teacher_ids: course.teacher_ids || [],
      tutor_image: course.tutor_image || null,
      remove_tutor_image: false
    })
  }

  const { avista, installment, totalInstallments } = useMemo(() => {
    const parsedFullPrice = formData.full_price_recorded ?? 0
    const parsedDiscount = +formData.discount_percentage_recorded.replace(/[^\d,]/g, '').replace(',', '.') || 0
    const avista = parsedFullPrice * (1 - parsedDiscount / 100)
    const installment = calculatePmt(installmentConfig.monthly_rate, installmentConfig.installments, avista)
    // Derivado de installment × nper para evitar o double-rounding do
    // parseFloat(installment.toFixed(2)) * 12 anterior.
    const totalInstallments = installment * installmentConfig.installments
    return { avista, installment, totalInstallments }
  }, [formData.full_price_recorded, formData.discount_percentage_recorded, installmentConfig])

  const errorCounts = countErrorsPerTab(COURSE_ONLINE_TAB_DEFS, errors)

  const showErrors = (next: Record<string, string[]>) => {
    setErrors(next)
    const offendingTab = firstTabWithErrors(COURSE_ONLINE_TAB_DEFS, next)
    if (offendingTab >= 0) setTabIndex(offendingTab)
  }

  // O select só lista os cursos ativos, mas o pacote guarda os inativos (a Eduzz continua
  // entregando), e o curso em edição também pode estar inativo. Fora das opções, o SearchableSelect
  // os esconderia, e o próximo onChange os tiraria do pacote.
  const packageCourses = [
    ...(editCourse?.id ? [{ id: editCourse.id, name: formData.name }] : []),
    ...savedPackageCourses
  ].reduce(
    (options, course) => (options.some((option) => option.id === course.id) ? options : [course, ...options]),
    packageCourseOptions
  )

  // Os professores que o curso já tem entram nas opções mesmo que a lista não os traga, para o próximo
  // onChange não os tirar da página sem querer.
  const teachers = savedTeachers.reduce(
    (options, teacher) => (options.some((option) => option.id === teacher.id) ? options : [...options, teacher]),
    teacherOptions
  )

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const listErrors = {
      ...validateModules(formData.modules),
      ...validateBonuses(formData.bonuses),
      ...validateTeachers(formData.teacher_ids),
      ...validateFaqs(formData.faqs)
    }
    if (Object.keys(listErrors).length > 0) {
      showErrors(listErrors)
      // Uma linha por regra que falhou, e não a lista de todas: dez módulos sem título dão uma só.
      toast.invalidForm(Array.from(new Set(Object.values(listErrors).flat())).join(' '))
      return
    }

    setSaving(true)
    setErrors({})
    try {
      if (editCourse && editCourse.id !== undefined) {
        await service.update(editCourse.id, formData)
        toast.success('Curso editado com sucesso!')
        onSuccess?.()
      } else {
        await service.create(formData)
        toast.success('Curso criado com sucesso!')
        onSuccess?.()
        setFormData(emptyForm())
        // Na criação o `editCourse` não muda, e o efeito que volta para a aba Geral não roda.
        setTabIndex(0)
      }
      onClose()
    } catch (error: unknown) {
      const title = editCourse && editCourse.id !== undefined ? 'Erro ao editar curso' : 'Erro ao criar curso'
      const apiErrors = (error as AxiosError<{ errors?: Record<string, string[]> }>).response?.data?.errors
      if (apiErrors) {
        showErrors(apiErrors)
        // Um campo que nenhuma aba mostra ficaria sem aviso nenhum: a mensagem dele vai no toast.
        const unmapped = unmappedErrorMessages(COURSE_ONLINE_TAB_DEFS, apiErrors)
        toast.error(title, unmapped.length > 0 ? unmapped.join(' ') : 'Corrija os erros indicados no formulário.')
      } else {
        toast.error(title, error)
      }
    } finally {
      setSaving(false)
    }
  }

  const clearForm = () => {
    setFormData(emptyForm())
    setErrors({})
    setTabIndex(0)
    onClose()
  }

  const generalTab = (
    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
      {/* name */}
      <GridItem>
        <FormField label="Nome" htmlFor="name" error={errors.name} isDisabled={saving}>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                name: e.target.value
              }))
            }
            type="text"
          />
        </FormField>
      </GridItem>

      {/* thumbnail */}
      <GridItem rowSpan={4}>
        <ImageCropInput
          label="Thumbnail"
          value={formData.thumbnail}
          onChange={(file) => setFormData((prev) => ({ ...prev, thumbnail: file }))}
          aspectRatio={450 / 210}
          suggestedSize="450px x 210px"
          previewAspectRatio="450 / 210"
          error={errors.thumbnail}
          isDisabled={saving}
        />
      </GridItem>

      {/* course link */}
      <GridItem>
        <FormField label="Link do curso" htmlFor="course_link" error={errors.course_link} isDisabled={saving}>
          <Input
            id="course_link"
            name="course_link"
            value={formData.course_link}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                course_link: e.target.value
              }))
            }
          />
        </FormField>
      </GridItem>

      {/* eduzz url */}
      <GridItem>
        <FormField label="URL Eduzz" htmlFor="eduzz_url" error={errors.eduzz_url} isDisabled={saving}>
          <Input
            id="eduzz_url"
            name="eduzz_url"
            value={formData.eduzz_url || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                eduzz_url: e.target.value
              }))
            }
            placeholder="https://www.eduzz.com/..."
          />
        </FormField>
      </GridItem>

      {/* knowledge area */}
      <GridItem>
        <FormField label="Área de conhecimento" error={errors.knowledge_area_ids} isDisabled={saving}>
          <Box>
            <SearchableSelect
              isMulti
              value={formData.knowledge_area_ids}
              options={knowledgeAreas.map((area) => ({ label: area.name, value: String(area.id) }))}
              onChange={(values) => setFormData({ ...formData, knowledge_area_ids: values })}
              placeholder="Selecione uma ou mais áreas"
              entity="área"
              feminine
            />
          </Box>
        </FormField>
      </GridItem>

      {/* dynamics */}
      <GridItem>
        <FormField label="Dinâmica de Ensino" error={errors.dynamic_ids} isDisabled={saving}>
          <SearchableSelect
            isMulti
            value={formData.dynamic_ids}
            options={dynamics.map((dynamic) => ({ label: dynamic.name, value: String(dynamic.id) }))}
            onChange={(values) => setFormData({ ...formData, dynamic_ids: values })}
            placeholder="Selecione uma ou mais dinâmicas"
            entity="dinâmica"
            feminine
          />
        </FormField>
      </GridItem>

      {/* campaign online */}
      <GridItem colSpan={2}>
        <Box>
          <FormField label="Listas do Active Campaign" error={errors.active_campaign_lists} isDisabled={saving}>
            <SearchableSelect
              isMulti
              value={formData.active_campaign_lists}
              options={activeCampaignLists.map((campaign) => ({ label: campaign.name, value: String(campaign.id) }))}
              onChange={(values) => setFormData({ ...formData, active_campaign_lists: values })}
              placeholder="Selecione uma ou mais listas"
              entity="lista"
              feminine
            />
          </FormField>
        </Box>
      </GridItem>

      {/* objectives */}
      <GridItem colSpan={2}>
        <FormField label="Objetivos do Curso" error={errors.objective_ids} isDisabled={saving}>
          <SearchableSelect
            isMulti
            value={formData.objective_ids}
            options={objectives.map((objective) => ({ label: objective.name, value: String(objective.id) }))}
            onChange={(values) => setFormData({ ...formData, objective_ids: values })}
            placeholder="Selecione um ou mais objetivos"
            entity="objetivo"
          />
        </FormField>
      </GridItem>

      {/* prices */}
      <GridItem colSpan={2}>
        <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
          <GridItem>
            <FormField
              label="Preço cheio"
              htmlFor="full_price_recorded"
              error={errors.full_price_recorded}
              isDisabled={saving}
            >
              <MoneyInput
                id="full_price_recorded"
                name="full_price_recorded"
                value={formData.full_price_recorded}
                onChange={(full_price_recorded) => setFormData((prev) => ({ ...prev, full_price_recorded }))}
                isDisabled={saving}
              />
            </FormField>
          </GridItem>
          {/* discount */}
          <GridItem>
            <FormField label="Desconto (%)" htmlFor="discount_percentage_recorded" isDisabled={saving}>
              <InputGroup>
                <Input
                  id="discount_percentage_recorded"
                  name="discount_percentage_recorded"
                  placeholder="0"
                  type="text"
                  value={formData.discount_percentage_recorded}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      discount_percentage_recorded: formatNumber(e.target.value)
                    }))
                  }
                />
                <InputRightAddon>%</InputRightAddon>
              </InputGroup>
            </FormField>
          </GridItem>
          {/* à vista com desconto */}
          <GridItem>
            <FormField label="À vista c/ Desconto" htmlFor="discounted_price">
              <InputGroup>
                <InputLeftAddon>R$</InputLeftAddon>
                <Input
                  id="discounted_price"
                  name="discounted_price"
                  value={avista.toFixed(2).replace('.', ',')}
                  readOnly
                />
              </InputGroup>
            </FormField>
          </GridItem>
          {/* parcelado com desconto */}
          <GridItem>
            <FormField label="Parcelado c/ Desconto" htmlFor="discounted_credit_price">
              <InputGroup>
                <InputLeftAddon>R$</InputLeftAddon>
                <Input id="discounted_credit_price" value={totalInstallments.toFixed(2).replace('.', ',')} readOnly />
              </InputGroup>
            </FormField>
          </GridItem>
          {/* parcela */}
          <GridItem>
            <FormField label={`Parcela (${installmentConfig.installments}x)`} htmlFor="installments">
              <InputGroup>
                <InputLeftAddon>R$</InputLeftAddon>
                <Input id="installments" value={installment.toFixed(2).replace('.', ',')} readOnly />
              </InputGroup>
            </FormField>
          </GridItem>
        </SimpleGrid>
      </GridItem>

      {/* CTA text */}
      <GridItem colSpan={2}>
        <FormField label="Texto CTA" htmlFor="cta_text_recorded" error={errors.cta_text_recorded} isDisabled={saving}>
          <Input
            id="cta_text_recorded"
            name="cta_text_recorded"
            placeholder="Ex: Matricule-se agora!"
            type="text"
            maxLength={255}
            value={formData.cta_text_recorded}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                cta_text_recorded: e.target.value
              }))
            }
          />
        </FormField>
      </GridItem>

      {/* duration (hours) */}
      <GridItem>
        <FormField
          label="Duração (horas)"
          htmlFor="duration_in_hours"
          error={errors.duration_in_hours}
          isDisabled={saving}
        >
          <Input
            id="duration_in_hours"
            name="duration_in_hours"
            value={formData.duration_in_hours}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                duration_in_hours: e.target.value
              }))
            }
            type="number"
          />
        </FormField>
      </GridItem>

      {/* duration (months) */}
      <GridItem>
        <FormField
          label="Duração (meses)"
          htmlFor="duration_in_months"
          error={errors.duration_in_months}
          isDisabled={saving}
        >
          <Input
            id="duration_in_months"
            name="duration_in_months"
            value={formData.duration_in_months}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                duration_in_months: e.target.value
              }))
            }
            type="number"
          />
        </FormField>
      </GridItem>

      {/* GreatPages timer title: the snippet's countdown is fixed, so it does not depend on the promotional bar */}
      <GridItem colSpan={2}>
        <FormField
          label="Título do cronômetro (GreatPages)"
          htmlFor="cronometer_title"
          error={errors.cronometer_title}
          helperText="Aparece na faixa do cronômetro da seção deste curso no GreatPages. Em branco, a faixa mostra só o cronômetro."
          isDisabled={saving}
        >
          <Input
            id="cronometer_title"
            name="cronometer_title"
            placeholder="Ex: Condição especial só até meia-noite"
            type="text"
            maxLength={255}
            value={formData.cronometer_title}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                cronometer_title: e.target.value
              }))
            }
          />
        </FormField>
      </GridItem>

      {/* promotional bar */}
      <GridItem colSpan={2}>
        <FormField
          label="Habilitar Barra de Promoção"
          htmlFor="promotional_bar"
          error={errors.promotional_bar}
          isDisabled={saving}
        >
          <Box
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            px={3}
            py={2}
            display="inline-block"
            cursor="pointer"
          >
            <Switch
              id="promotional_bar"
              name="promotional_bar"
              isChecked={formData.promotional_bar === '1'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  promotional_bar: e.target.checked ? '1' : '0',
                  cronometer_type: 'disabled',
                  cronometer_end_date: '',
                  cronometer_time: ''
                }))
              }
            />
          </Box>
        </FormField>
      </GridItem>

      {formData.promotional_bar === '1' && (
        <>
          <GridItem colSpan={2}>
            <FormField label="Cronômetro" htmlFor="cronometer_enabled" isDisabled={saving}>
              <Box
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                px={3}
                py={2}
                display="inline-block"
                cursor="pointer"
              >
                <Switch
                  id="cronometer_enabled"
                  name="cronometer_enabled"
                  isChecked={formData.cronometer_type !== 'disabled'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cronometer_type: e.target.checked ? '' : 'disabled',
                      cronometer_end_date: '',
                      cronometer_time: ''
                    }))
                  }
                />
              </Box>
            </FormField>
          </GridItem>

          {formData.cronometer_type !== 'disabled' && (
            <>
              <GridItem colSpan={1}>
                <FormField
                  label="Tipo de Cronômetro"
                  htmlFor="cronometer_type"
                  error={errors.cronometer_type}
                  isDisabled={saving}
                >
                  <FormSelect
                    id="cronometer_type"
                    name="cronometer_type"
                    value={formData.cronometer_type ?? ''}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        cronometer_type: value,
                        cronometer_end_date: '',
                        cronometer_time: ''
                      }))
                    }
                    options={[
                      { value: 'end_date', label: 'Data de término' },
                      { value: 'time', label: 'Renovação automática' }
                    ]}
                    placeholder="Selecione o tipo"
                    isDisabled={saving}
                  />
                </FormField>
              </GridItem>

              {formData.cronometer_type === 'end_date' && (
                <GridItem colSpan={1}>
                  <FormField
                    label="Data de término"
                    htmlFor="cronometer_end_date"
                    error={errors.cronometer_end_date}
                    isDisabled={saving}
                  >
                    <Input
                      id="cronometer_end_date"
                      name="cronometer_end_date"
                      type="datetime-local"
                      value={
                        formData.cronometer_end_date
                          ? new Date(formData.cronometer_end_date).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cronometer_end_date: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              )}

              {formData.cronometer_type === 'time' && (
                <GridItem colSpan={1}>
                  <FormField
                    label="Renovação automática (dias)"
                    htmlFor="cronometer_time"
                    error={errors.cronometer_time}
                    isDisabled={saving}
                  >
                    <Input
                      id="cronometer_time"
                      name="cronometer_time"
                      type="number"
                      value={formData.cronometer_time || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cronometer_time: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              )}
            </>
          )}
        </>
      )}
    </Grid>
  )

  const tabLabel = (index: number) => (
    <TabErrorLabel label={COURSE_ONLINE_TAB_DEFS[index].label} count={errorCounts[index]} />
  )

  return (
    <FormModal
      isOpen={isOpen}
      // O `clearForm` ficava só no botão Fechar; aqui ele vale também para o X e para o
      // clique fora, que antes deixavam o formulário preenchido para a próxima abertura.
      onClose={() => {
        clearForm()
        onClose()
      }}
      title={`${editCourse ? 'Editar ' : 'Adicionar novo '}curso`}
      onSubmit={handleSave}
      size="editor"
      cancelLabel="Fechar"
      isLoading={saving}
      isConfirmDisabled={loading}
      // Um erro numa aba fechada travava o Salvar em silêncio: a API valida e leva o erro à aba.
      noValidate
      tabIndex={tabIndex}
      onTabChange={setTabIndex}
      tabs={
        loading
          ? undefined
          : [
              { label: tabLabel(0), content: generalTab },
              {
                label: tabLabel(1),
                content: (
                  <CourseOnlinePassaporteTab
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    courses={packageCourses}
                    installmentConfig={installmentConfig}
                    disabled={saving}
                  />
                )
              },
              {
                label: tabLabel(2),
                content: (
                  <CoursePageTab
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    heroDescriptionHelp="Vazia, o site mostra: Aprenda {nome do curso} com os professores da ABRA, no seu ritmo: aulas gravadas, acompanhamento de tutores e certificado ao concluir."
                    disabled={saving}
                  />
                )
              },
              {
                label: tabLabel(3),
                content: (
                  <CourseModulesEditor
                    modules={formData.modules}
                    onChange={(modules) => setFormData((prev) => ({ ...prev, modules }))}
                    disabled={saving}
                    fieldErrors={errors}
                  />
                )
              },
              {
                label: tabLabel(4),
                content: (
                  <CourseBonusesEditor
                    bonuses={formData.bonuses}
                    onChange={(bonuses) => setFormData((prev) => ({ ...prev, bonuses }))}
                    disabled={saving}
                    fieldErrors={errors}
                  />
                )
              },
              {
                label: tabLabel(5),
                content: (
                  <CourseOnlineTeachersTab
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    teachers={teachers}
                    isLoadingTeachers={loadingTeachers}
                    disabled={saving}
                  />
                )
              },
              {
                label: tabLabel(6),
                content: (
                  <FaqEditor
                    faqs={formData.faqs}
                    onChange={(faqs) => setFormData((prev) => ({ ...prev, faqs }))}
                    disabled={saving}
                    fieldErrors={errors}
                  />
                )
              }
            ]
      }
    >
      {loading && <LoadingBlock />}
    </FormModal>
  )
}

export default CoursesOnlineModal
